import { useState, useEffect } from "react";
import axios from "axios";
import { Users, Trash2, Edit2, ToggleLeft, ToggleRight, UserPlus, Key, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABELS = {
  admin: { label: "Admin", color: "bg-red-500/20 text-red-300", desc: "Acces complet" },
  collaborateur: { label: "Collaborateur", color: "bg-[#2395E3]/20 text-[#2395E3]", desc: "Modifier contenu" },
  webmaster: { label: "Webmaster", color: "bg-purple-500/20 text-purple-300", desc: "Gestion site web" },
  partenaire: { label: "Partenaire", color: "bg-[#00CCC2]/20 text-[#00CCC2]", desc: "Clients propres + CA" },
};

const EMPTY_FORM = { email: "", role: "collaborateur", name: "", auth_type: "local", password: "" };

const UserManager = ({ headers }) => {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formError, setFormError] = useState("");

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`, { headers });
      setUsers(res.data);
    } catch (err) { console.error(err); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const openCreate = () => {
    setEditingUser(null);
    setForm({ ...EMPTY_FORM });
    setFormError("");
    setShowForm(true);
  };

  const openEdit = (user) => {
    setEditingUser(user);
    setForm({
      email: user.email || "",
      name: user.name || "",
      role: user.role || "collaborateur",
      auth_type: "local",
      password: "",
    });
    setFormError("");
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingUser(null);
    setFormError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    if (!editingUser && (!form.password || form.password.length < 6)) {
      setFormError("Le mot de passe doit contenir au moins 6 caracteres");
      return;
    }
    try {
      if (editingUser) {
        const payload = { name: form.name, email: form.email, role: form.role };
        if (form.password) payload.password = form.password;
        await axios.put(`${API}/admin/users/${editingUser.user_id}`, payload, { headers });
      } else {
        await axios.post(`${API}/admin/users`, { ...form, auth_type: "local" }, { headers });
      }
      closeForm();
      fetchUsers();
    } catch (err) {
      setFormError(err.response?.data?.detail || "Erreur");
    }
  };

  const toggleUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle`, {}, { headers });
      fetchUsers();
    } catch (err) { console.error(err); }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm("Supprimer cet utilisateur et ses sessions ?")) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers });
      fetchUsers();
    } catch (err) { console.error(err); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold" data-testid="users-title">Gestion des Utilisateurs</h1>
        <button onClick={openCreate} data-testid="create-user-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#2395E3] hover:bg-[#2395E3]/80 text-white text-sm font-medium transition-colors">
          <UserPlus className="w-4 h-4" /> Creer un utilisateur
        </button>
      </div>

      {/* Create / Edit form */}
      {showForm && (
        <form onSubmit={handleSubmit} data-testid="user-form" className="mb-6 p-5 rounded-xl bg-[#131B2F] border border-[#2395E3]/30 space-y-4">
          <div className="flex justify-between items-center">
            <p className="font-semibold text-[#2395E3] text-sm">
              {editingUser ? `Modifier : ${editingUser.name || editingUser.email}` : "Creer un utilisateur"}
            </p>
            <button type="button" onClick={closeForm} className="p-1 hover:bg-white/10 rounded-lg"><X className="w-4 h-4 text-slate-400" /></button>
          </div>
          {formError && <p className="text-red-400 text-sm" data-testid="user-form-error">{formError}</p>}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs text-slate-400 mb-1">Email *</label>
              <input type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-[#0B1121] border border-white/10 outline-none text-sm focus:border-[#2395E3]"
                placeholder="utilisateur@entreprise.com" data-testid="user-form-email" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Nom</label>
              <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-[#0B1121] border border-white/10 outline-none text-sm focus:border-[#2395E3]" placeholder="Nom affiche" data-testid="user-form-name" />
            </div>
            <div>
              <label className="block text-xs text-slate-400 mb-1">Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2 rounded-lg bg-[#0B1121] border border-white/10 outline-none text-sm" data-testid="user-form-role">
                {Object.entries(ROLE_LABELS).map(([key, val]) => (
                  <option key={key} value={key}>{val.label} -- {val.desc}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs text-slate-400 mb-1">
              {editingUser ? "Nouveau mot de passe (laisser vide pour ne pas changer)" : "Mot de passe *"}
            </label>
            <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-[#0B1121] border border-white/10 outline-none text-sm focus:border-[#00CCC2]"
              placeholder="Minimum 6 caracteres" data-testid="user-form-password"
              required={!editingUser} />
          </div>

          <div className="flex gap-3">
            <button type="submit" className="px-5 py-2 rounded-lg bg-[#00CCC2] hover:bg-[#00CCC2]/80 text-white text-sm font-medium" data-testid="user-form-submit">
              {editingUser ? "Enregistrer" : "Creer"}
            </button>
            <button type="button" onClick={closeForm} className="px-4 py-2 rounded-lg bg-white/10 text-slate-300 text-sm">Annuler</button>
          </div>
        </form>
      )}

      {/* Info box */}
      <div className="mb-6 p-4 rounded-xl bg-[#131B2F] border border-white/10 text-sm space-y-3">
        <p className="font-semibold text-[#2395E3]">Authentification locale</p>
        <p className="text-slate-400">Chaque utilisateur est cree avec un compte <strong className="text-[#00CCC2]">local</strong> (email + mot de passe). Le super admin utilise les identifiants Basic Auth.</p>
        <div className="flex flex-wrap gap-4">
          {Object.entries(ROLE_LABELS).map(([key, val]) => (
            <div key={key} className="flex items-center gap-2">
              <span className={`px-2 py-0.5 text-xs rounded-full ${val.color}`}>{val.label}</span>
              <span className="text-xs text-slate-500">{val.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="p-3 rounded-xl bg-[#131B2F] border border-white/10 text-center">
          <div className="text-xl font-bold text-[#2395E3]">{users.length}</div>
          <div className="text-xs text-slate-400">Total</div>
        </div>
        {Object.entries(ROLE_LABELS).map(([key, val]) => (
          <div key={key} className="p-3 rounded-xl bg-[#131B2F] border border-white/10 text-center">
            <div className={`text-xl font-bold ${val.color.split(" ")[1]}`}>{users.filter(u => u.role === key).length}</div>
            <div className="text-xs text-slate-400">{val.label}s</div>
          </div>
        ))}
      </div>

      {/* Users list */}
      <div className="space-y-3">
        {users.map((user) => {
          const role = ROLE_LABELS[user.role] || ROLE_LABELS.collaborateur;
          return (
            <div key={user.user_id} data-testid={`user-row-${user.user_id}`} className={`p-4 rounded-xl bg-[#131B2F] border border-white/10 ${!user.active ? "opacity-50" : ""}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0 bg-[#00CCC2]/20">
                  <Key className="w-5 h-5 text-[#00CCC2]" />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{user.name || user.email}</h3>
                  <p className="text-sm text-slate-400 truncate">{user.email}</p>
                </div>
                <span className={`px-3 py-1 text-xs rounded-full shrink-0 ${role.color}`}>{role.label}</span>
                <span className="px-2 py-0.5 text-xs rounded-full shrink-0 bg-[#00CCC2]/10 text-[#00CCC2]">Local</span>
                <button onClick={() => openEdit(user)} className="p-2 hover:bg-[#2395E3]/20 rounded-lg" title="Modifier" data-testid={`user-edit-${user.user_id}`}>
                  <Edit2 className="w-4 h-4 text-[#2395E3]" />
                </button>
                <button onClick={() => toggleUser(user.user_id)} className="p-2 hover:bg-white/10 rounded-lg" title={user.active ? "Desactiver" : "Activer"} data-testid={`user-toggle-${user.user_id}`}>
                  {user.active !== false ? <ToggleRight className="w-5 h-5 text-green-400" /> : <ToggleLeft className="w-5 h-5 text-red-400" />}
                </button>
                <button onClick={() => deleteUser(user.user_id)} className="p-2 hover:bg-red-500/20 rounded-lg" data-testid={`user-delete-${user.user_id}`}>
                  <Trash2 className="w-4 h-4 text-red-400" />
                </button>
              </div>
              <div className="flex gap-4 mt-2 text-xs text-slate-500">
                {user.created_at && <span>Cree: {new Date(user.created_at).toLocaleDateString("fr-FR")}</span>}
                <span className="text-[#00CCC2]/80">Compte local (email/mot de passe)</span>
              </div>
            </div>
          );
        })}
        {users.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 mb-2">Aucun utilisateur</p>
            <p className="text-sm text-slate-600">Cliquez sur "Creer un utilisateur" pour ajouter un collaborateur.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserManager;
